/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  FormControl,
  IconButton,
  FormHelperText as MuiFormHelperText,
  styled,
  InputAdornment,
  Typography,
  TextField,
  Link,
} from '@mui/material';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/Button';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {LockHirist, UnlockHirist} from '@jobseeker/lotus/assets/icons';
import React from 'react';
import * as yup from 'yup';
import {Input} from '@jobseeker/lotus/elements';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import Dialog from '@jobseeker/lotus/components/DialogWithBottomSheet';
import {useAlertCallback} from '@jobseeker/lotus/hooks';

const validationSchemaChangePassword = yup.object({
  newEmail: yup
    .string()
    .email('Please enter a valid email address')
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Please enter a valid email address'
    )
    .required('Please enter your new email'),
  password: yup
    .string()
    .required('Please enter your password')
    .matches(/^\S{6,32}$/, 'Password must be 6-32 characters with no spaces'),
});

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));

export const EmailAddressComponent: React.FC<object> = ({}) => {
  const dispatch = useDispatch();
  const profile = useSelector((state: AppState) => state.user.profile);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [isResendDialogOpen, setResentDialogOpen] = useState(false);

  const showConfirmPassword = () => {
    setShowConfirmPass(prev => !prev);
  };

  // const onChange = (values: {newEmail: string}) => {
  //   if (values.newEmail) {
  //     setAlertHeaders({
  //       title: 'Please enter your password to confirm the change',
  //       subtitle: `To proceed with changing your email, we'll send a verification to ${values.newEmail}`,
  //     });
  //   }
  // };

  const formik = useFormik({
    initialValues: {
      newEmail: '',
      password: '',
    },
    validationSchema: validationSchemaChangePassword,
    onSubmit: async values => {
      try {
        const resp = await userService.changeEmail(values);
        if (resp?.error) {
          formik.setFieldError('password', resp.error.message);
        } else {
          dispatch({
            type: 'User/set_user_profile',
            payload: {
              settings: {
                emailChangeRequestedOn: values.newEmail,
              },
            },
          });
          toggleEmailDialog();
          toggleResendEmailDialog();
        }
      } catch (error) {
        enqueueSnackbar('Failed to change email. Please try again later.', {
          variant: 'error',
        });
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  const cancelEmailChangeRequest = async () => {
    try {
      const resp = await userService.cancelEmailChange();
      if (resp.error) {
        enqueueSnackbar(resp.error.message, {variant: 'error'});
      } else {
        dispatch({
          type: 'User/set_user_profile',
          payload: {
            settings: {
              emailChangeRequestedOn: false,
            },
          },
        });
        enqueueSnackbar('Email change request cancelled successfully', {
          variant: 'success',
        });
        setShowConfirmPass(false);
      }
    } catch (error) {
      enqueueSnackbar(
        'Failed to cancel email change request. Please try again later.',
        {variant: 'error'}
      );
    }
  };

  const onEmailSubmit = async () => {
    formik.setFieldTouched('newEmail', true, true);
    await formik.validateField('newEmail');

    if (formik.errors.newEmail || formik.values.newEmail.trim() === '') {
      return;
    }

    setIsEmailSubmitted(true);
    toggleEmailDialog();
  };

  const [ResendEmailDialog, toggleResendEmailDialog] = useAlertCallback({
    title: (
      <Typography fontSize={20} fontWeight={500} color="#000" mb={1}>
        Resend Verification
      </Typography>
    ),
    forHirist: true,
    subtitle: (
      <Typography
        fontSize={{xxs: 16, md: 18}}
        fontWeight={200}
        lineHeight={1.68}
        color={'#000'}
        sx={{opacity: 0.7}}
      >
        We have sent a verification mail on{' '}
        {profile?.settings?.emailChangeRequestedOn}. To complete the change,
        please click on the verification link in the mail. Meanwhile, you can
        continue accessing your account by using your current Email ie{' '}
        {profile.email}.
      </Typography>
    ),
    primaryAction: 'Okay',
    BodyContent: () => (
      <Box
        bgcolor={'#fffcf7'}
        px={1.25}
        py={1}
        mt={2.25}
        border={'1px solid #fbf0dd'}
      >
        <Typography fontStyle={'italic'} variant="body2">
          If you don&apos;t see the email, check other places it might be, like
          your junk, spam, social, or other folders.
        </Typography>
      </Box>
    ),
    primaryCallback: console.log,
    alert: true,
  });

  const toggleEmailDialog = async () => {
    setResentDialogOpen(prev => !prev);
  };

  const resendEmail = async () => {
    await userService.resentChangeEmail();
  };

  return (
    <>
      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={700}>
          Email Address
        </Typography>
        <Box
          display={'flex'}
          gap={2.25}
          flexDirection={{xxs: 'column', md: 'row'}}
          alignItems={{xxs: 'flex-end', md: 'center'}}
        >
          <TextField
            value={profile?.email}
            disabled
            fullWidth
            sx={{width: {xxs: '100%', md: '40%'}}}
            InputProps={{
              sx: {
                backgroundColor: '#f9f9f9',
              },
            }}
          />

          <Link
            sx={{
              fontSize: {xxs: '12px', lg: '16px'},
              cursor: 'pointer',
              height: 'fit-content',
              color: '#e9630c',
              textDecoration: 'underline',
              textDecorationColor: '#e9630c',
              textUnderlineOffset: '2px',
            }}
            onClick={() => showConfirmPassword()}
            data-testid="open_in_mobile_app"
          >
            Change Email?
          </Link>
        </Box>
        {showConfirmPass || !!profile?.settings?.emailChangeRequestedOn ? (
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant="subtitle2"
              sx={{marginTop: {xxs: 1, md: 3.125}}}
              gutterBottom
              fontWeight={700}
            >
              Enter New Email Address
            </Typography>
            <Box
              display={'flex'}
              alignItems={{xxs: 'flex-start', md: 'center'}}
              gap={{xxs: 6.875, md: 2.25}}
              flexDirection={{xxs: 'column', md: 'row'}}
            >
              <FormControl
                sx={{width: {xxs: '100%', md: '40%'}}}
                error={
                  !profile?.settings?.emailChangeRequestedOn &&
                  formik.touched.newEmail &&
                  Boolean(formik.errors.newEmail)
                }
              >
                <Input
                  variant={VersionVariants.VERSION_ONE}
                  type={'email'}
                  name="newEmail"
                  value={
                    profile?.settings?.emailChangeRequestedOn
                      ? profile.settings.emailChangeRequestedOn
                      : formik.values.newEmail
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ccc',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ccc',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: {xxs: '#E59700', md: '#000'},
                      },
                    },
                  }}
                  onChange={formik.handleChange}
                  error={
                    !profile?.settings?.emailChangeRequestedOn &&
                    formik.touched.newEmail &&
                    Boolean(formik.errors.newEmail)
                  }
                  placeholder="Enter your new email address"
                  InputProps={{autoComplete: 'new-email'}}
                  disabled={!!profile?.settings?.emailChangeRequestedOn}
                />
                {!!!profile?.settings?.emailChangeRequestedOn &&
                  formik.touched.newEmail &&
                  Boolean(formik.errors.newEmail) && (
                    <Box display={'flex'}>
                      <FormHelperText>
                        <>{formik?.errors?.newEmail || ''}</>
                      </FormHelperText>
                    </Box>
                  )}
              </FormControl>

              <Box sx={{display: 'flex', gap: 2.5, alignItems: 'center'}}>
                <Button
                  sx={{
                    fontSize: {xxs: '12px', lg: '16px'},
                    backgroundColor: '#e9630c',
                    fontWeight: 500,
                    px: {xxs: 2, md: 5},
                    py: 1.25,
                    height: 'fit-content',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: '#e79e00',
                    },
                  }}
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (profile?.settings?.emailChangeRequestedOn) {
                      toggleResendEmailDialog();
                      resendEmail();
                    } else {
                      onEmailSubmit();
                    }
                  }}
                  data-testid="open_in_mobile_app"
                >
                  {profile?.settings?.emailChangeRequestedOn
                    ? 'Resend Verification'
                    : 'Verify Email'}
                </Button>
                <Button
                  sx={{
                    fontSize: {xxs: '12px', lg: '16px'},
                    height: 'fit-content',
                    color: '#e9630c',
                  }}
                  variant="text"
                  size="small"
                  onClick={
                    profile?.settings?.emailChangeRequestedOn
                      ? cancelEmailChangeRequest
                      : () => {
                          setShowConfirmPass(false);
                        }
                  }
                  data-testid="open_in_mobile_app"
                >
                  {profile?.settings?.emailChangeRequestedOn
                    ? 'Cancel Change'
                    : 'Cancel'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <Dialog
        useBottomSheet={false}
        open={isResendDialogOpen}
        onClose={toggleEmailDialog}
        title={
          <Typography fontSize={20} fontWeight={700}>
            Confirm your Password
          </Typography>
        }
        alert={true}
        subTitle={
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: '200',
            }}
          >
            For changing your Email we will send a verification mail on{' '}
            <Typography
              component="span"
              sx={{
                fontSize: '18px',
                fontWeight: '400',
              }}
            >
              {formik.values.newEmail}
            </Typography>
          </Typography>
        }
        headerStyles={{boxShadow: 'none', border: 'none'}}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '8px',
          },
        }}
      >
        <Box mb={2} mt={2.125} display={'flex'} flexDirection={'column'}>
          <Typography
            mt={-1}
            fontSize={{xxs: 14, md: 16}}
            variant="body1"
            color={'secondary.dark'}
          >
            Please provide your password to confirm changing your Email
          </Typography>
          <Box display={'flex'} mt={3.5} justifyContent={'space-between'}>
            <Typography fontSize={14} fontWeight={700}>
              Password
            </Typography>
          </Box>
          <FormControl
            sx={{
              width: '100%',
              mt: 0.5,
              display: !isEmailSubmitted ? 'none' : 'block',
            }}
            error={formik.touched.password && Boolean(formik.errors.password)}
          >
            <Input
              variant={VersionVariants.VERSION_ONE}
              type={showNewPassword ? 'text' : 'password'}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              placeholder="Enter the password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#ccc',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ccc',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E59700',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <Image
                          src={UnlockHirist}
                          width={15}
                          height={15}
                          alt="show password"
                        />
                      ) : (
                        <Image
                          src={LockHirist}
                          width={15}
                          height={15}
                          alt="hide password"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                autoComplete: 'new-password',
              }}
            />

            <Box display={'flex'} justifyContent={'space-between'}>
              <FormHelperText
                sx={{
                  opacity:
                    formik.touched.password && Boolean(formik.errors.password)
                      ? 1
                      : 0,
                  width: '100%',
                }}
              >
                <>{formik?.errors?.password || ''}</>
              </FormHelperText>
            </Box>
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: {xxs: 3.125, md: 4.375},
              flexDirection: {xxs: 'column', md: 'row'},
            }}
          >
            <Button
              sx={{
                width: {xxs: '100%', md: 'auto'},
                fontSize: '16px',
                backgroundColor: '#e9630c',
                borderRadius: '3px',
                px: 5,
                py: 1.25,
                height: 'fit-content',
                '&:hover': {
                  backgroundColor: '#e79e00',
                },
              }}
              variant="contained"
              size="small"
              onClick={() => {
                formik.handleSubmit();
              }}
              data-testid="open_in_mobile_app"
            >
              Confirm
            </Button>
            <Button
              sx={{
                fontSize: '16px',
                height: 'fit-content',
                color: '#e9630c',
              }}
              variant="text"
              size="small"
              onClick={toggleEmailDialog}
              data-testid="open_in_mobile_app"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
      <ResendEmailDialog />
    </>
  );
};
